import { FormEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { api, formatBytes, formatDate, patch, post, remove } from './api';
import { Banner, DashboardShell, Empty, Metric, Status, type ShellConfig } from './ui';

// Operations dashboard for admin.mailbots.top (behind Cloudflare Access).
// It only ever shows metadata and counts: no subjects, bodies, OTPs or payloads.

type CheckStatus = 'ok' | 'warn' | 'down' | 'off';
type Check = { id: string; label: string; status: CheckStatus; detail: string };
type Row = Record<string, any>;
type Relay = {
  id: string; name: string; region: string; status: string; disk_total_bytes: number | null; disk_used_bytes: number | null;
  queued_messages: number | null; last_heartbeat_at: number | null; heartbeat_age: number | null; version: string | null;
};
type Overview = {
  generatedAt: number;
  health: Check[];
  users: { total: number; verified: number; new24h: number; new7d: number; activeSessions: number; activeUsers24h: number };
  organizations: Record<string, number>;
  subscriptions: { mrrUsd: number; active: number; pastDue: number; cancelScheduled: number; renewing7d: number; byPlan: Record<string, number>; byProvider: Record<string, number>; byStatus: Record<string, number> };
  domains: { byStatus: Record<string, number>; failed: number };
  mailboxes: { total: number; active: number; usedBytes: number; quotaBytes: number };
  paidMail: { stored: number; storedBytes: number; last24h: number; monthAccepted: number; monthRejected: number; monthBytes: number };
  developer: { apiKeysActive: number; apiKeysRevoked: number; apiKeysUsed24h: number; endpointsActive: number; endpointsDisabled: number; endpointsFailing: number; deliveries24h: Record<string, number> };
  billing: { enabled: boolean; environment: string; pendingUsdt: number; oldestUsdtAt: number | null; paddleEvents24h: number; paddleErrors24h: number; lastPaddleEventAt: number | null };
  free: {
    owners: Record<string, { total: number; new24h: number; new7d: number }>; activeBoxes: Record<string, number>;
    boxesCreated24h: number; storedMessages: number; messages24h: number;
    extensionBySource: Record<string, number>;
    activation: { registered: number; withBox: number; withMail: number };
  } | null;
  relays: Relay[];
  abuse: { denylist: number };
  acquisition: { windowDays: number; total: number; byEvent: Record<string, number>; byPlacement: Record<string, number>; byPage: Record<string, number>; byReferral: Record<string, Record<string, number>> };
  audit: { last24h: number; recent: Row[] };
  series: { days: string[]; tracked: boolean; signups: number[]; paidAccepted: number[]; paidRejected: number[]; freeActiveBoxes: number[]; freeOwnersByKind: Record<string, number[]> };
  config: { flags: Record<string, boolean | string>; secrets: Record<string, boolean> };
};
type AdminConfig = ShellConfig & { plans: Record<'starter' | 'pro', { messagesPerMonth: number; storageBytes: number }> };

const TABS = ['overview', 'health', 'customers', 'billing', 'mail', 'developer', 'relays', 'free service', 'abuse', 'audit'];
const CHECK_TAB: Record<string, string> = {
  billing: 'billing', payments: 'billing', subscriptions: 'billing', domains: 'mail', webhooks: 'developer',
  relay: 'relays', relay_api: 'relays', relay_heartbeat: 'relays', relay_disk: 'relays', relay_queue: 'relays',
  free_db: 'free service', free_api: 'free service',
};
const STATUS_TEXT: Record<CheckStatus, [string, string]> = { ok: ['✓', 'OK'], warn: ['!', 'Warning'], down: ['✕', 'Down'], off: ['–', 'Off'] };
const TRACKING_NOTE = 'Daily tracking starts once migration 0004 is applied.';

const num = (value: number | null | undefined) => Number(value || 0).toLocaleString('en-US');
const plural = (count: number, one: string, many = `${one}s`) => `${num(count)} ${count === 1 ? one : many}`;
const total = (values: number[]) => values.reduce((sum, value) => sum + value, 0);
const dayLabel = (day: string) => new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
function since(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined) return 'never';
  if (seconds < 90) return `${Math.max(0, Math.round(seconds))} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

const Loading = () => <div className="empty"><p>Loading…</p></div>;

function HealthBadge({ status }: { status: CheckStatus }) {
  const [icon, text] = STATUS_TEXT[status];
  return <span className={`health-badge ${status}`}><span aria-hidden="true">{icon}</span>{text}</span>;
}

function overall(checks: Check[]): CheckStatus {
  return checks.some((check) => check.status === 'down') ? 'down' : checks.some((check) => check.status === 'warn') ? 'warn' : 'ok';
}

function healthSummary(checks: Check[]) {
  const down = checks.filter((check) => check.status === 'down').length;
  const warn = checks.filter((check) => check.status === 'warn').length;
  if (down) return `${plural(down, 'check')} failing${warn ? ` · ${plural(warn, 'warning')}` : ''}`;
  return warn ? plural(warn, 'warning') : 'All systems operational';
}

function useRows(path: string, key: string, refreshKey: number) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState('');
  const reload = useCallback(() => api<Row>(path)
    .then((body) => { setRows(body[key] || []); setError(''); })
    .catch((e: Error) => setError(e.message)), [path, key]);
  useEffect(() => { void reload(); }, [reload, refreshKey]);
  return { rows, error, reload };
}

function useAction() {
  const [error, setError] = useState('');
  const run = useCallback(async (task: () => Promise<unknown>) => {
    try { setError(''); await task(); } catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); }
  }, []);
  return { error, run };
}

export function AdminApp({ config }: { config: AdminConfig }) {
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [, setClock] = useState(0);
  const load = useCallback(() => {
    setLoading(true);
    setRefreshKey((key) => key + 1);
    return api<Overview>('/api/admin/overview')
      .then((body) => { setData(body); setLoadedAt(Date.now()); setError(''); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    void load();
    const refresh = setInterval(() => void load(), 60_000);
    const clock = setInterval(() => setClock((value) => value + 1), 10_000);
    return () => { clearInterval(refresh); clearInterval(clock); };
  }, [load]);

  const checks = data?.health || [];
  const state = overall(checks);
  const footStatus = data ? <><span className={`status-dot ${state}`} />{healthSummary(checks)}</> : <><span className="status-dot" />Checking…</>;
  return <DashboardShell admin config={config} title="Operations" tab={tab} setTab={setTab} tabs={TABS} status={footStatus}>
    <div className="admin-toolbar">
      <span className={`live-dot${loading ? ' busy' : ''}`} aria-hidden="true" />
      <span>{loadedAt ? `Updated ${since((Date.now() - loadedAt) / 1000)} ago` : 'Loading…'}</span>
      <span className="toolbar-note">Auto-refresh every 60 s</span>
      <button className="button small secondary" onClick={() => void load()} disabled={loading}>Refresh</button>
    </div>
    {error && <Banner>{error === 'admin_access_required' ? 'Admin host requires Cloudflare Access and an allowlisted email.' : error}</Banner>}
    <div className={loading && data ? 'is-refreshing' : undefined}>
      {tab === 'overview' && <OverviewTab data={data} go={setTab} />}
      {tab === 'health' && <HealthTab data={data} go={setTab} />}
      {tab === 'customers' && <CustomersTab refreshKey={refreshKey} />}
      {tab === 'billing' && <BillingTab data={data} refreshKey={refreshKey} />}
      {tab === 'mail' && <MailTab data={data} config={config} refreshKey={refreshKey} />}
      {tab === 'developer' && <DeveloperTab data={data} refreshKey={refreshKey} />}
      {tab === 'relays' && <RelaysTab data={data} />}
      {tab === 'free service' && <FreeTab data={data} />}
      {tab === 'abuse' && <AbuseTab refreshKey={refreshKey} />}
      {tab === 'audit' && <AuditTab refreshKey={refreshKey} />}
    </div>
  </DashboardShell>;
}

/* ---------- Overview ---------- */

function OverviewTab({ data, go }: { data: Overview | null; go: (tab: string) => void }) {
  if (!data) return <Loading />;
  const subscriptions = data.subscriptions;
  const freeUsers = data.free ? total(Object.values(data.free.owners).map((owner) => owner.total)) : 0;
  const freeActive = data.free ? total(Object.values(data.free.activeBoxes)) : 0;
  const inSetup = total(['pending', 'verified', 'provisioning'].map((key) => data.domains.byStatus[key] || 0));
  const deliveries = data.developer.deliveries24h;
  const note = data.series.tracked ? undefined : TRACKING_NOTE;
  return <>
    <HealthStrip checks={data.health} onOpen={() => go('health')} />
    <div className="metric-grid">
      <Metric label="Monthly recurring revenue" value={`$${num(subscriptions.mrrUsd)}`} note={`${plural(subscriptions.active, 'active subscription')} · ${num(subscriptions.pastDue)} past due`} />
      <Metric label="Console users" value={num(data.users.total)} note={`+${num(data.users.new7d)} this week · ${num(data.users.activeUsers24h)} active today`} />
      <Metric label="Paid mail, 24 h" value={num(data.paidMail.last24h)} note={`${num(data.paidMail.monthAccepted)} accepted · ${num(data.paidMail.monthRejected)} rejected this month`} />
      <Metric label="Free service" value={data.free ? num(freeActive) : '—'} note={data.free ? `active addresses · ${plural(freeUsers, 'user')}` : 'FREE_DB is not bound'} />
    </div>
    <div className="metric-grid">
      <Metric label="Organizations" value={num(data.organizations.active || 0)} note={`${num(data.organizations.suspended || 0)} suspended`} />
      <Metric label="Customer domains" value={num(data.domains.byStatus.active || 0)} note={`${num(inSetup)} in setup · ${num(data.domains.failed)} failed`} />
      <Metric label="Mailbox storage" value={formatBytes(data.mailboxes.usedBytes)} note={`of ${formatBytes(data.mailboxes.quotaBytes)} · ${plural(data.mailboxes.active, 'mailbox', 'mailboxes')}`} />
      <Metric label="Webhooks, 24 h" value={num(deliveries.delivered || 0)} note={`${num(deliveries.failed || 0)} failed · ${num(deliveries.pending || 0)} pending`} />
    </div>
    <div className="panel-grid">
      <AttentionPanel checks={data.health} go={go} />
      <ActivityPanel rows={data.audit.recent} count={data.audit.last24h} go={go} />
    </div>
    <AcquisitionPanel data={data.acquisition} tracked={data.series.tracked} />
    <ReferralTable data={data.acquisition} />
    <div className="chart-grid">
      <BarChart title="Console sign-ups" subtitle="New accounts per day" days={data.series.days} series={[{ label: 'Sign-ups', values: data.series.signups, slot: 1 }]} />
      <BarChart title="Paid messages" subtitle="Accepted and rejected per day" days={data.series.days} note={note}
        series={[{ label: 'Accepted', values: data.series.paidAccepted, slot: 1 }, { label: 'Rejected', values: data.series.paidRejected, slot: 2 }]} />
      <BarChart title="Free service sign-ups" subtitle="New bot and extension users per day" days={data.series.days} series={freeSeries(data)} />
      <BarChart title="Free active addresses" subtitle="Daily peak of live addresses" days={data.series.days} note={note} gauge
        series={[{ label: 'Active addresses', values: data.series.freeActiveBoxes, slot: 1 }]} />
    </div>
  </>;
}

// Conversion counters from /api/events. These are daily totals with no visitor
// identity attached, so the panel can only ever show aggregates.
const EVENT_LABELS: Record<string, string> = {
  chrome_click: 'Chrome install', edge_click: 'Edge install', firefox_click: 'Firefox install',
  telegram_click: 'Telegram open', signup: 'Sign-up', checkout: 'Checkout started',
};

function AcquisitionPanel({ data, tracked }: { data: Overview['acquisition']; tracked: boolean }) {
  const rank = (values: Record<string, number>) => Object.entries(values).sort((a, b) => b[1] - a[1]);
  const label = (key: string) => EVENT_LABELS[key] || key.replace(/[_-]/g, ' ');
  return <section className="panel">
    <div className="panel-title">
      <div><small>ACQUISITION</small><h2>CTA conversions</h2></div>
      <span>{data.windowDays} d</span>
    </div>
    {!tracked ? <p>{TRACKING_NOTE}</p> : !data.total ? <Empty text="No CTA events recorded yet." /> : <div className="acquisition-grid">
      {([['Event', data.byEvent], ['Placement', data.byPlacement], ['Page', data.byPage]] as const).map(([heading, values]) => <div key={heading}>
        <small>{heading.toUpperCase()}</small>
        <dl>{rank(values).map(([key, value]) => <div key={key}><dt>{label(key)}</dt><dd>{num(value)}</dd></div>)}</dl>
      </div>)}
    </div>}
  </section>;
}

// Where the traffic came from, for links carrying ?ref=. Visits are counted against
// the campaign in the URL on every arrival, while conversions are credited to the first
// campaign a visitor ever arrived through — traffic and credit answer different
// questions, so the two columns will not reconcile row by row.
function ReferralTable({ data }: { data: Overview['acquisition'] }) {
  const campaigns = Object.keys(data.byReferral).sort((a, b) => (data.byReferral[b].visit || 0) - (data.byReferral[a].visit || 0));
  const sum = (campaign: string, events: string[]) => total(events.map((event) => data.byReferral[campaign][event] || 0));
  return <DataTable title="By referral" eyebrow={`Acquisition · ${data.windowDays} d`} rows={campaigns} rowKey={(campaign) => campaign} columns={[
    { key: 'campaign', label: 'Campaign', render: (campaign) => <b className="capitalize">{campaign}</b> },
    { key: 'visit', label: 'Visits', align: 'right', render: (campaign) => num(data.byReferral[campaign].visit) },
    { key: 'install', label: 'Installs', align: 'right', render: (campaign) => num(sum(campaign, ['chrome_click', 'edge_click', 'firefox_click'])) },
    { key: 'telegram', label: 'Telegram', align: 'right', render: (campaign) => num(data.byReferral[campaign].telegram_click) },
    { key: 'signup', label: 'Sign-ups', align: 'right', render: (campaign) => num(data.byReferral[campaign].signup) },
    { key: 'checkout', label: 'Checkouts', align: 'right', render: (campaign) => num(data.byReferral[campaign].checkout) },
  ]} empty="No ?ref= traffic yet. Add ?ref=<campaign> to the links you post." />;
}

function HealthStrip({ checks, onOpen }: { checks: Check[]; onOpen: () => void }) {
  const state = overall(checks);
  return <button type="button" className={`health-strip ${state}`} onClick={onOpen}>
    <HealthBadge status={state} />
    <b>{healthSummary(checks)}</b>
    <span className="health-dots" aria-hidden="true">{checks.map((check) => <i key={check.id} className={`hd ${check.status}`} title={`${check.label}: ${STATUS_TEXT[check.status][1]}`} />)}</span>
    <span className="health-open">View all checks →</span>
  </button>;
}

function AttentionPanel({ checks, go }: { checks: Check[]; go: (tab: string) => void }) {
  const items = checks.filter((check) => check.status === 'down' || check.status === 'warn')
    .sort((a, b) => (a.status === 'down' ? 0 : 1) - (b.status === 'down' ? 0 : 1));
  return <section className="panel">
    <div className="panel-title"><div><small>Needs attention</small><h2>{items.length ? plural(items.length, 'item') : 'Nothing to do'}</h2></div></div>
    {items.length
      ? <ul className="attention">{items.map((check) => <li key={check.id}><HealthBadge status={check.status} /><span><b>{check.label}</b> — {check.detail}</span><button onClick={() => go(CHECK_TAB[check.id] || 'health')}>Open</button></li>)}</ul>
      : <Empty text="All checks are passing." />}
  </section>;
}

function ActivityPanel({ rows, count, go }: { rows: Row[]; count: number; go: (tab: string) => void }) {
  return <section className="panel">
    <div className="panel-title"><div><small>Activity</small><h2>{plural(count, 'event')} in 24 h</h2></div><button onClick={() => go('audit')}>Audit log</button></div>
    {rows.length
      ? <ul className="activity">{rows.map((row) => <li key={row.id}><div><b>{row.action}</b><small>{row.actor_type} · {row.actor_id || 'system'}</small></div><time>{formatDate(row.created_at)}</time></li>)}</ul>
      : <Empty text="No activity recorded yet." />}
  </section>;
}

function freeSeries(data: Overview): ChartSeries[] {
  const byKind = data.series.freeOwnersByKind;
  const zeros = data.series.days.map(() => 0);
  const others = Object.entries(byKind).filter(([kind]) => kind !== 'telegram' && kind !== 'extension');
  const series: ChartSeries[] = [
    { label: 'Telegram', values: byKind.telegram || zeros, slot: 1 },
    { label: 'Extension', values: byKind.extension || zeros, slot: 2 },
  ];
  if (others.length) series.push({ label: 'Other', values: zeros.map((_, index) => total(others.map(([, values]) => values[index] || 0))), slot: 3 });
  return series;
}

/* ---------- Chart ---------- */

type ChartSeries = { label: string; values: number[]; slot: 1 | 2 | 3 };

function niceMax(value: number) {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return ([1, 2, 5, 10].find((factor) => factor * magnitude >= value) || 10) * magnitude;
}

function roundedTop(x: number, y: number, width: number, height: number, radius: number) {
  const r = Math.min(radius, height, width / 2);
  return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
}

// `gauge` series (daily peaks) headline their latest value; summing them is meaningless.
function BarChart({ title, subtitle, days, series, note, gauge = false }: { title: string; subtitle: string; days: string[]; series: ChartSeries[]; note?: string; gauge?: boolean }) {
  const [active, setActive] = useState<number | null>(null);
  const totals = days.map((_, index) => total(series.map((item) => item.values[index] || 0)));
  const headline = gauge ? totals[totals.length - 1] || 0 : total(totals);
  const peakValue = Math.max(0, ...totals);
  const peak = totals.indexOf(peakValue);
  const top = niceMax(peakValue);
  const width = 560, height = 200, left = 36, right = 8, topPad = 20, bottom = 26;
  const plotWidth = width - left - right, plotHeight = height - topPad - bottom, baseY = topPad + plotHeight;
  const band = plotWidth / days.length;
  const barWidth = Math.min(24, band * 0.62);
  const scale = (value: number) => (value / top) * plotHeight;
  const barX = (index: number) => left + band * index + (band - barWidth) / 2;
  const labelled = [...new Set([0, Math.floor((days.length - 1) / 2), days.length - 1])];
  const tipLeft = active === null ? 0 : Math.min(88, Math.max(12, ((left + band * active + band / 2) / width) * 100));
  return <section className="panel chart-card">
    <div className="chart-head">
      <div><h3>{title}</h3><p>{subtitle}</p></div>
      <div className="chart-total"><strong>{num(headline)}</strong><small>{gauge ? 'today' : `last ${days.length} days`}</small></div>
    </div>
    {series.length > 1 && <ul className="legend">{series.map((item) => <li key={item.label}><span className={`swatch s${item.slot}`} aria-hidden="true" />{item.label}</li>)}</ul>}
    <div className="chart-wrap" onMouseLeave={() => setActive(null)}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${title}, last ${days.length} days: ${gauge ? `${num(headline)} today` : `${num(headline)} in total`}${peakValue ? `, peak ${num(peakValue)} on ${dayLabel(days[peak])}` : ''}`}>
        {[top, top / 2, 0].map((tick) => <g key={tick}>
          <line className={tick === 0 ? 'base' : 'grid'} x1={left} x2={width - right} y1={baseY - scale(tick)} y2={baseY - scale(tick)} />
          {Number.isInteger(tick) && <text className="tick" x={left - 8} y={baseY - scale(tick) + 4} textAnchor="end">{num(tick)}</text>}
        </g>)}
        {days.map((day, index) => {
          let cursor = baseY;
          const parts: Array<{ key: string; slot: number; y: number; height: number }> = [];
          for (const item of series) {
            const value = item.values[index] || 0;
            if (value <= 0) continue;
            const gap = cursor < baseY ? 2 : 0;
            const segment = Math.max(1, scale(value) - gap);
            const y = cursor - gap - segment;
            parts.push({ key: item.label, slot: item.slot, y, height: segment });
            cursor = y;
          }
          return <g key={day} className={`col${active === index ? ' active' : ''}`}>
            {parts.map((part, position) => position === parts.length - 1
              ? <path key={part.key} className={`bar s${part.slot}`} d={roundedTop(barX(index), part.y, barWidth, part.height, 4)} />
              : <rect key={part.key} className={`bar s${part.slot}`} x={barX(index)} y={part.y} width={barWidth} height={part.height} />)}
            <rect className="hit" x={left + band * index} y={topPad} width={band} height={plotHeight} tabIndex={0}
              aria-label={`${dayLabel(day)}: ${series.map((item) => `${item.label} ${num(item.values[index] || 0)}`).join(', ')}`}
              onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} onBlur={() => setActive(null)} />
          </g>;
        })}
        {peakValue > 0 && <text className="peak" x={barX(peak) + barWidth / 2} y={baseY - scale(peakValue) - 6} textAnchor="middle">{num(peakValue)}</text>}
        {labelled.map((index) => <text key={index} className="tick" x={left + band * index + band / 2} y={height - 6} textAnchor="middle">{dayLabel(days[index])}</text>)}
      </svg>
      {active !== null && <div className="chart-tip" style={{ left: `${tipLeft}%` }}>
        <strong>{num(totals[active])}</strong>
        <span>{dayLabel(days[active])}</span>
        {series.length > 1 && series.map((item) => <span key={item.label}><i className={`key s${item.slot}`} aria-hidden="true" />{item.label} · {num(item.values[active] || 0)}</span>)}
      </div>}
    </div>
    {note && <p className="chart-note">{note}</p>}
    <details className="chart-table">
      <summary>View as table</summary>
      <div className="table-scroll"><table className="data-table">
        <thead><tr><th>Day</th>{series.map((item) => <th key={item.label} className="right">{item.label}</th>)}{series.length > 1 && <th className="right">Total</th>}</tr></thead>
        <tbody>{days.map((day, index) => <tr key={day}><td>{dayLabel(day)}</td>{series.map((item) => <td key={item.label} className="right">{num(item.values[index] || 0)}</td>)}{series.length > 1 && <td className="right">{num(totals[index])}</td>}</tr>)}</tbody>
      </table></div>
    </details>
  </section>;
}

/* ---------- Health ---------- */

function HealthTab({ data, go }: { data: Overview | null; go: (tab: string) => void }) {
  if (!data) return <Loading />;
  return <>
    <DataTable title="Health checks" eyebrow="Live probes" rows={data.health} rowKey={(check) => check.id} columns={[
      { key: 'status', label: 'Status', render: (check) => <HealthBadge status={check.status} /> },
      { key: 'check', label: 'Check', render: (check) => <b>{check.label}</b> },
      { key: 'detail', label: 'Detail', render: (check) => check.detail },
      { key: 'open', label: '', align: 'right', render: (check) => CHECK_TAB[check.id] ? <button onClick={() => go(CHECK_TAB[check.id])}>Open</button> : null },
    ]} />
    <section className="panel">
      <div className="panel-title"><div><small>Configuration</small><h2>Flags</h2></div><span>generated {formatDate(data.generatedAt)}</span></div>
      <ul className="config-list">{Object.entries(data.config.flags).map(([name, value]) => <li key={name}><code>{name}</code><span>{typeof value === 'boolean' ? (value ? 'on' : 'off') : value}</span></li>)}</ul>
      <h3 className="subhead">Secrets &amp; bindings</h3>
      <ul className="config-list">{Object.entries(data.config.secrets).map(([name, present]) => <li key={name}><code>{name}</code><span className={present ? 'ok-text' : 'bad-text'}>{present ? '✓ set' : '✕ missing'}</span></li>)}</ul>
    </section>
  </>;
}

/* ---------- Customers ---------- */

function SearchRow({ placeholder, onSearch }: { placeholder: string; onSearch: (value: string) => void }) {
  const [draft, setDraft] = useState('');
  return <form className="filter-row" role="search" onSubmit={(event: FormEvent) => { event.preventDefault(); onSearch(draft.trim()); }}>
    <input type="search" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={placeholder} aria-label={placeholder} />
    <button className="button small">Search</button>
    {draft && <button type="button" className="button small secondary" onClick={() => { setDraft(''); onSearch(''); }}>Clear</button>}
  </form>;
}

function CustomersTab({ refreshKey }: { refreshKey: number }) {
  const [query, setQuery] = useState('');
  const suffix = query ? `?q=${encodeURIComponent(query)}` : '';
  const organizations = useRows(`/api/admin/organizations${suffix}`, 'organizations', refreshKey);
  const users = useRows(`/api/admin/users${suffix}`, 'users', refreshKey);
  const action = useAction();
  const toggle = (row: Row) => action.run(async () => {
    const next = row.status === 'suspended' ? 'active' : 'suspended';
    if (next === 'suspended' && !confirm(`Suspend ${row.name}? Its domains stop receiving mail.`)) return;
    await patch(`/api/admin/organizations/${row.id}/status`, { status: next });
    await organizations.reload();
  });
  const error = action.error || organizations.error || users.error;
  return <>
    <SearchRow placeholder="Search by organization, name or email" onSearch={setQuery} />
    {error && <Banner>{error}</Banner>}
    <DataTable title="Organizations" eyebrow="Customers" rows={organizations.rows} rowKey={(row) => row.id} columns={[
      { key: 'name', label: 'Organization', render: (row) => <><b>{row.name}</b><small>{row.email}</small></> },
      { key: 'status', label: 'Status', render: (row) => <Status value={row.status} /> },
      { key: 'plan', label: 'Plan', render: (row) => row.plan ? <>{row.plan}<small>{String(row.subscription_status || '').replace(/_/g, ' ')}{row.current_period_end ? ` · until ${formatDate(row.current_period_end)}` : ''}</small></> : <span className="muted">Free</span> },
      { key: 'created', label: 'Created', render: (row) => formatDate(row.created_at) },
      { key: 'actions', label: '', align: 'right', render: (row) => <button onClick={() => void toggle(row)}>{row.status === 'suspended' ? 'Restore' : 'Suspend'}</button> },
    ]} />
    <DataTable title="Users" eyebrow="Accounts" rows={users.rows} rowKey={(row) => row.id} columns={[
      { key: 'user', label: 'User', render: (row) => <><b>{row.name || '—'}</b><small>{row.email}</small></> },
      { key: 'verified', label: 'Email', render: (row) => row.email_verified ? <span className="ok-text">✓ verified</span> : <span className="muted">unverified</span> },
      { key: 'methods', label: 'Sign-in', render: (row) => String(row.providers || '—').replace('credential', 'password') },
      { key: 'orgs', label: 'Orgs', align: 'right', render: (row) => num(row.organizations) },
      { key: 'seen', label: 'Last seen', render: (row) => formatDate(row.last_seen_at) },
      { key: 'joined', label: 'Joined', render: (row) => formatDate(row.created_at) },
    ]} />
  </>;
}

/* ---------- Billing ---------- */

function BillingTab({ data, refreshKey }: { data: Overview | null; refreshKey: number }) {
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [usdtStatus, setUsdtStatus] = useState('pending');
  const subscriptions = useRows(`/api/admin/subscriptions${subscriptionStatus ? `?status=${subscriptionStatus}` : ''}`, 'subscriptions', refreshKey);
  const payments = useRows(`/api/admin/usdt-payments?status=${usdtStatus}`, 'payments', refreshKey);
  const events = useRows('/api/admin/billing-events', 'events', refreshKey);
  const action = useAction();
  const review = (row: Row, decision: 'approve' | 'reject') => action.run(async () => {
    const verb = decision === 'approve' ? 'Approve' : 'Reject';
    if (!confirm(`${verb} ${row.expected_amount_usd} USDT (${row.requested_plan}) from ${row.organization_name}? Verify wallet, token, network and amount on-chain first.`)) return;
    await post(`/api/admin/usdt-payments/${row.id}/${decision}`, {});
    await payments.reload();
  });
  const error = action.error || subscriptions.error || payments.error || events.error;
  return <>
    {data && <div className="metric-grid">
      <Metric label="Monthly recurring revenue" value={`$${num(data.subscriptions.mrrUsd)}`} note={Object.entries(data.subscriptions.byPlan).map(([plan, count]) => `${count} ${plan}`).join(' · ') || 'no paying customers yet'} />
      <Metric label="Past due" value={num(data.subscriptions.pastDue)} note="3-day grace, then expired" />
      <Metric label="Renewing in 7 days" value={num(data.subscriptions.renewing7d)} note={`${num(data.subscriptions.cancelScheduled)} set to cancel`} />
      <Metric label="Paddle events, 24 h" value={num(data.billing.paddleEvents24h)} note={`${num(data.billing.paddleErrors24h)} not applied · ${data.billing.environment}`} />
    </div>}
    {error && <Banner>{error}</Banner>}
    <DataTable title="Subscriptions" eyebrow="Billing" rows={subscriptions.rows} rowKey={(row) => row.id}
      toolbar={<div className="filter-row"><select value={subscriptionStatus} onChange={(event) => setSubscriptionStatus(event.target.value)} aria-label="Filter by status">
        <option value="">All statuses</option>{['active', 'past_due', 'cancelled', 'expired', 'pending'].map((value) => <option key={value} value={value}>{value.replace('_', ' ')}</option>)}
      </select></div>}
      columns={[
        { key: 'org', label: 'Organization', render: (row) => <><b>{row.organization_name}</b><small>{row.owner_email}</small></> },
        { key: 'plan', label: 'Plan', render: (row) => <>{row.plan}<small>{row.provider}</small></> },
        { key: 'status', label: 'Status', render: (row) => <><Status value={row.status} />{row.cancel_at_period_end ? <small>cancels at period end</small> : null}</> },
        { key: 'period', label: 'Period ends', render: (row) => <>{formatDate(row.current_period_end)}{row.grace_until ? <small>grace until {formatDate(row.grace_until)}</small> : null}</> },
        { key: 'updated', label: 'Updated', render: (row) => formatDate(row.updated_at) },
      ]} />
    <DataTable title="USDT payments" eyebrow="Manual review" rows={payments.rows} rowKey={(row) => row.id}
      toolbar={<div className="filter-row"><select value={usdtStatus} onChange={(event) => setUsdtStatus(event.target.value)} aria-label="Filter payments">
        {['pending', 'approved', 'rejected'].map((value) => <option key={value} value={value}>{value}</option>)}
      </select></div>}
      columns={[
        { key: 'amount', label: 'Amount', render: (row) => <><b>{num(row.expected_amount_usd)} USDT</b><small>{row.requested_plan} · {row.network}</small></> },
        { key: 'org', label: 'Organization', render: (row) => <>{row.organization_name}<small>{row.owner_email}</small></> },
        { key: 'tx', label: 'Transaction', render: (row) => <a className="mono-link" href={`https://tronscan.org/#/transaction/${row.tx_hash}`} target="_blank" rel="noopener noreferrer"><code>{String(row.tx_hash).slice(0, 10)}…{String(row.tx_hash).slice(-8)}</code></a> },
        { key: 'submitted', label: 'Submitted', render: (row) => formatDate(row.submitted_at) },
        { key: 'review', label: '', align: 'right', render: (row) => row.status === 'pending'
          ? <div className="row-actions"><button onClick={() => void review(row, 'approve')}>Approve</button><button onClick={() => void review(row, 'reject')}>Reject</button></div>
          : <small>{row.status} by {row.reviewed_by || '—'} · {formatDate(row.reviewed_at)}</small> },
      ]} empty={`No ${usdtStatus} payments.`} />
    <DataTable title="Paddle webhook events" eyebrow="Provider log" rows={events.rows} rowKey={(row) => `${row.provider}:${row.event_id}`} columns={[
      { key: 'event', label: 'Event', render: (row) => <><b>{row.event_name}</b><small>{row.provider}</small></> },
      { key: 'id', label: 'Event ID', render: (row) => <code>{String(row.event_id).slice(0, 26)}</code> },
      { key: 'received', label: 'Received', render: (row) => formatDate(row.received_at) },
      { key: 'result', label: 'Result', render: (row) => !row.error ? <span className="ok-text">applied</span> : row.error === 'non_subscription_event' ? <span className="muted">ignored</span> : <span className="bad-text">{row.error}</span> },
    ]} empty="No billing webhooks received yet." />
  </>;
}

/* ---------- Mail ---------- */

function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const tone = pct >= 95 ? ' down' : pct >= 80 ? ' warn' : '';
  return <div className="meter-cell">
    <div className={`meter${tone}`} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}><i style={{ width: `${pct}%` }} /></div>
    <small>{Math.round(pct)}%</small>
  </div>;
}

function MailTab({ data, config, refreshKey }: { data: Overview | null; config: AdminConfig; refreshKey: number }) {
  const domains = useRows('/api/admin/domains', 'domains', refreshKey);
  const mailboxes = useRows('/api/admin/mailboxes', 'mailboxes', refreshKey);
  const usage = useRows('/api/admin/usage', 'usage', refreshKey);
  const action = useAction();
  const toggle = (row: Row) => action.run(async () => {
    const next = row.status === 'suspended' ? 'active' : 'suspended';
    if (next === 'suspended' && !confirm(`Suspend ${row.hostname}? It stops receiving mail.`)) return;
    await patch(`/api/admin/domains/${row.id}/status`, { status: next });
    await domains.reload();
  });
  const step = (done: unknown, label: string) => <span className={`step-chip${done ? ' done' : ''}`}>{done ? '✓' : '·'} {label}</span>;
  const error = action.error || domains.error || mailboxes.error || usage.error;
  return <>
    {data && <div className="metric-grid">
      <Metric label="Active domains" value={num(data.domains.byStatus.active || 0)} note={`${num(data.domains.failed)} failed · ${num(data.domains.byStatus.suspended || 0)} suspended`} />
      <Metric label="Mailboxes" value={num(data.mailboxes.active)} note={`${num(data.mailboxes.total)} provisioned`} />
      <Metric label="Stored now" value={num(data.paidMail.stored)} note={`${formatBytes(data.paidMail.storedBytes)} · 72 h retention`} />
      <Metric label="Rejected this month" value={num(data.paidMail.monthRejected)} note={`of ${num(data.paidMail.monthAccepted + data.paidMail.monthRejected)} received`} />
    </div>}
    {error && <Banner>{error}</Banner>}
    <DataTable title="Customer domains" eyebrow="Mail routing" rows={domains.rows} rowKey={(row) => row.id} columns={[
      { key: 'host', label: 'Domain', render: (row) => <><b>{row.hostname}</b><small>{row.organization_name} · {row.owner_email}</small></> },
      { key: 'status', label: 'Status', render: (row) => <Status value={row.status} /> },
      { key: 'steps', label: 'Verification', render: (row) => <div className="step-chips">{step(row.txt_verified_at, 'TXT')}{step(row.mx_verified_at, 'MX')}{step(row.probe_verified_at, 'Probe')}{step(row.provisioned_at, 'Mailbox')}</div> },
      { key: 'error', label: 'Last error', render: (row) => row.last_error ? <span className="bad-text">{row.last_error}</span> : <span className="muted">—</span> },
      { key: 'actions', label: '', align: 'right', render: (row) => <button onClick={() => void toggle(row)}>{row.status === 'suspended' ? 'Restore' : 'Suspend'}</button> },
    ]} empty="No customer domains yet." />
    <DataTable title="Mailboxes" eyebrow="Storage" rows={mailboxes.rows} rowKey={(row) => row.id} columns={[
      { key: 'login', label: 'Mailbox', render: (row) => <><code>{row.login}</code><small>{row.hostname}</small></> },
      { key: 'org', label: 'Organization', render: (row) => row.organization_name },
      { key: 'status', label: 'Status', render: (row) => <Status value={row.status} /> },
      { key: 'storage', label: 'Storage', render: (row) => <><Meter value={row.used_bytes} max={row.quota_bytes} label={`${row.login} storage`} /><small>{formatBytes(row.used_bytes)} of {formatBytes(row.quota_bytes)}</small></> },
    ]} empty="No mailboxes provisioned yet." />
    <DataTable title="Usage this month" eyebrow="Quota" rows={usage.rows} rowKey={(row) => row.id} columns={[
      { key: 'org', label: 'Organization', render: (row) => <><b>{row.organization_name}</b><small>{row.plan || 'no plan'}</small></> },
      { key: 'accepted', label: 'Accepted', render: (row) => {
        const limit = config.plans[row.plan as 'starter' | 'pro']?.messagesPerMonth || 0;
        return <><Meter value={row.accepted_messages} max={limit} label={`${row.organization_name} monthly quota`} /><small>{num(row.accepted_messages)} of {limit ? num(limit) : '—'}</small></>;
      } },
      { key: 'rejected', label: 'Rejected', align: 'right', render: (row) => num(row.rejected_messages) },
      { key: 'bytes', label: 'Volume', align: 'right', render: (row) => formatBytes(row.accepted_bytes) },
    ]} empty="No paid mail received this month." />
  </>;
}

/* ---------- Developer ---------- */

function DeveloperTab({ data, refreshKey }: { data: Overview | null; refreshKey: number }) {
  const keys = useRows('/api/admin/api-keys', 'apiKeys', refreshKey);
  const webhooks = useRows('/api/admin/webhooks', 'endpoints', refreshKey);
  const deliveries = useRows('/api/admin/webhooks', 'deliveries', refreshKey);
  const action = useAction();
  const [notice, setNotice] = useState('');
  const now = Date.now() / 1000;
  const keyState = (row: Row) => row.revoked_at ? 'revoked' : row.expires_at && row.expires_at < now ? 'expired' : 'active';
  const revoke = (row: Row) => action.run(async () => {
    if (!confirm(`Revoke API key "${row.name}" of ${row.organization_name}? Integrations using it stop working immediately.`)) return;
    await post(`/api/admin/api-keys/${row.id}/revoke`, {});
    await keys.reload();
  });
  const toggleWebhook = (row: Row) => action.run(async () => {
    const status = row.status === 'active' ? 'disabled' : 'active';
    if (status === 'disabled' && !confirm(`Disable webhook ${row.host} for ${row.organization_name}? Pending deliveries will pause.`)) return;
    await patch(`/api/admin/webhooks/${row.id}/status`, { status });
    await Promise.all([webhooks.reload(), deliveries.reload()]);
    setNotice(`Webhook ${row.host} is now ${status}.`);
  });
  const retryDelivery = (row: Row) => action.run(async () => {
    await post(`/api/admin/webhook-deliveries/${row.id}/retry`, {});
    await Promise.all([webhooks.reload(), deliveries.reload()]);
    setNotice(`Retry started for ${row.event_type} delivery to ${row.host}.`);
  });
  const error = action.error || keys.error || webhooks.error || deliveries.error;
  return <>
    {data && <div className="metric-grid">
      <Metric label="Active API keys" value={num(data.developer.apiKeysActive)} note={`${num(data.developer.apiKeysUsed24h)} used today · ${num(data.developer.apiKeysRevoked)} revoked`} />
      <Metric label="Webhook endpoints" value={num(data.developer.endpointsActive)} note={`${num(data.developer.endpointsFailing)} failing · ${num(data.developer.endpointsDisabled)} disabled`} />
      <Metric label="Delivered, 24 h" value={num(data.developer.deliveries24h.delivered || 0)} note={`${num(data.developer.deliveries24h.pending || 0)} pending`} />
      <Metric label="Failed, 24 h" value={num(data.developer.deliveries24h.failed || 0)} note="after all retries" />
    </div>}
    {error && <Banner>{error}</Banner>}
    {notice && <div role="status"><Banner tone="info">{notice}</Banner></div>}
    <DataTable title="API keys" eyebrow="Developer API" rows={keys.rows} rowKey={(row) => row.id} columns={[
      { key: 'name', label: 'Key', render: (row) => <><b>{row.name}</b><small>{row.organization_name}</small></> },
      { key: 'prefix', label: 'Prefix', render: (row) => <code>mb_live_{row.key_prefix}_••••</code> },
      { key: 'scopes', label: 'Scopes', render: (row) => String(row.scopes || '').split(',').join(', ') },
      { key: 'used', label: 'Last used', render: (row) => formatDate(row.last_used_at) },
      { key: 'status', label: 'Status', render: (row) => <Status value={keyState(row)} /> },
      { key: 'actions', label: '', align: 'right', render: (row) => keyState(row) === 'active' ? <button onClick={() => void revoke(row)}>Revoke</button> : null },
    ]} empty="No API keys created yet." />
    <DataTable title="Webhook endpoints" eyebrow="Signed delivery" rows={webhooks.rows} rowKey={(row) => row.id} columns={[
      { key: 'host', label: 'Endpoint', render: (row) => <><b>{row.host}</b><small>{row.organization_name}</small></> },
      { key: 'status', label: 'Status', render: (row) => <Status value={row.status} /> },
      { key: 'failures', label: 'Failures', align: 'right', render: (row) => <>{num(row.failure_count)}<small>{row.last_failure_at ? formatDate(row.last_failure_at) : ''}</small></> },
      { key: 'success', label: 'Last success', render: (row) => formatDate(row.last_success_at) },
      { key: 'actions', label: '', align: 'right', render: (row) => <button onClick={() => void toggleWebhook(row)}>{row.status === 'active' ? 'Disable' : 'Enable'}</button> },
    ]} empty="No webhook endpoints configured." />
    <DataTable title="Undelivered webhooks" eyebrow="Retry queue" rows={deliveries.rows} rowKey={(row) => row.id} columns={[
      { key: 'event', label: 'Event', render: (row) => <><b>{row.event_type}</b><small>{row.host} · {row.organization_name}</small></> },
      { key: 'status', label: 'Status', render: (row) => <Status value={row.status} /> },
      { key: 'attempts', label: 'Attempts', align: 'right', render: (row) => num(row.attempts) },
      { key: 'next', label: 'Next attempt', render: (row) => row.status === 'pending' ? formatDate(row.next_attempt_at) : '—' },
      { key: 'error', label: 'Last error', render: (row) => row.last_error ? <span className="bad-text">{row.last_error}</span> : '—' },
      { key: 'actions', label: '', align: 'right', render: (row) => <button onClick={() => void retryDelivery(row)}>Retry now</button> },
    ]} empty="Every webhook has been delivered." />
  </>;
}

/* ---------- Relays ---------- */

function RelaysTab({ data }: { data: Overview | null }) {
  if (!data) return <Loading />;
  const relayChecks = data.health.filter((check) => check.id.startsWith('relay'));
  return <>
    <DataTable title="Gateway checks" eyebrow="Mail gateway" rows={relayChecks} rowKey={(check) => check.id} columns={[
      { key: 'status', label: 'Status', render: (check) => <HealthBadge status={check.status} /> },
      { key: 'check', label: 'Check', render: (check) => <b>{check.label}</b> },
      { key: 'detail', label: 'Detail', render: (check) => check.detail },
    ]} />
    {data.relays.length
      ? <div className="relay-grid">{data.relays.map((relay) => {
          const used = relay.disk_used_bytes || 0;
          const capacity = relay.disk_total_bytes || 0;
          return <section className="panel" key={relay.id}>
            <div className="panel-title"><div><small>{relay.region}</small><h2>{relay.name}</h2></div><Status value={relay.status} /></div>
            <dl className="kv">
              <dt>Last heartbeat</dt><dd>{relay.heartbeat_age === null ? 'never' : `${since(relay.heartbeat_age)} ago`}</dd>
              <dt>Queued messages</dt><dd>{num(relay.queued_messages)}</dd>
              <dt>Agent version</dt><dd>{relay.version || '—'}</dd>
              <dt>Disk</dt><dd>{formatBytes(used)} of {formatBytes(capacity)}</dd>
            </dl>
            <Meter value={used} max={capacity} label={`${relay.name} disk usage`} />
          </section>;
        })}</div>
      : <section className="panel"><Empty text="No VPS relay has reported yet." /></section>}
  </>;
}

/* ---------- Free service ---------- */

function FreeTab({ data }: { data: Overview | null }) {
  if (!data) return <Loading />;
  if (!data.free) return <section className="panel"><Empty text="Bind the free-service database (FREE_DB) to see bot and extension metrics." /></section>;
  const free = data.free;
  const kinds = [...new Set([...Object.keys(free.owners), ...Object.keys(free.activeBoxes)])];
  const users = total(Object.values(free.owners).map((owner) => owner.total));
  const note = data.series.tracked ? undefined : TRACKING_NOTE;
  return <>
    <div className="metric-grid">
      <Metric label="Registered devices" value={num(users)} note={`+${num(total(Object.values(free.owners).map((owner) => owner.new7d)))} this week · not people`} />
      <Metric label="Reached an inbox" value={num(free.activation.withBox)} note={`of ${plural(free.activation.registered, 'device')} registered`} />
      <Metric label="Received mail" value={num(free.activation.withMail)} note="devices that have seen a message arrive" />
      <Metric label="Active addresses" value={num(total(Object.values(free.activeBoxes)))} note={`${num(free.boxesCreated24h)} created in 24 h`} />
      <Metric label="Extension inbox" value={num(free.storedMessages)} note={`${num(free.messages24h)} received in 24 h · 24 h TTL`} />
      <Metric label="Free API" value={STATUS_TEXT[data.health.find((check) => check.id === 'free_api')?.status || 'off'][1]} note={data.health.find((check) => check.id === 'free_api')?.detail || ''} />
    </div>
    <div className="chart-grid">
      <BarChart title="New free users" subtitle="Telegram and extension sign-ups per day" days={data.series.days} series={freeSeries(data)} />
      <BarChart title="Active addresses" subtitle="Daily peak of live addresses" days={data.series.days} note={note} gauge
        series={[{ label: 'Active addresses', values: data.series.freeActiveBoxes, slot: 1 }]} />
    </div>
    <DataTable title="By client" eyebrow="Free service" rows={kinds} rowKey={(kind) => kind} columns={[
      { key: 'kind', label: 'Client', render: (kind) => <b className="capitalize">{kind}</b> },
      { key: 'users', label: 'Users', align: 'right', render: (kind) => num(free.owners[kind]?.total) },
      { key: 'new24', label: 'New 24 h', align: 'right', render: (kind) => num(free.owners[kind]?.new24h) },
      { key: 'new7', label: 'New 7 d', align: 'right', render: (kind) => num(free.owners[kind]?.new7d) },
      { key: 'active', label: 'Active addresses', align: 'right', render: (kind) => num(free.activeBoxes[kind]) },
    ]} empty="No free-service devices yet." />
    <StoreTable counts={free.extensionBySource} />
  </>;
}

// Store attribution for extension devices. A row is one install that called the API,
// so reinstalls and extra browser profiles each add one: read it as install events,
// never as people. 'unknown' is a device that registered before the extension started
// reporting its build, and clears as those devices check in again.
const STORE_LABELS: Record<string, string> = { chrome: 'Chrome Web Store', edge: 'Edge Add-ons', firefox: 'Firefox Add-ons', unknown: 'Unattributed' };

function StoreTable({ counts }: { counts: Record<string, number> }) {
  const rows = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const all = total(Object.values(counts));
  return <DataTable title="By store" eyebrow="Extension installs" rows={rows} rowKey={(source) => source} columns={[
    { key: 'store', label: 'Store', render: (source) => <b>{STORE_LABELS[source] || source}</b> },
    { key: 'n', label: 'Installs', align: 'right', render: (source) => num(counts[source]) },
    { key: 'share', label: 'Share', align: 'right', render: (source) => (all ? `${Math.round((counts[source] / all) * 100)}%` : '—') },
  ]} empty="No extension installs recorded yet." />;
}

/* ---------- Abuse & audit ---------- */

function AbuseTab({ refreshKey }: { refreshKey: number }) {
  const entries = useRows('/api/admin/denylist', 'entries', refreshKey);
  const [pattern, setPattern] = useState('');
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<'address' | 'domain'>('domain');
  const action = useAction();
  const add = (event: FormEvent) => {
    event.preventDefault();
    void action.run(async () => {
      await post('/api/admin/denylist', { kind, pattern, reason });
      setPattern('');
      setReason('');
      await entries.reload();
    });
  };
  const unblock = (row: Row) => action.run(async () => { await remove(`/api/admin/denylist/${row.id}`); await entries.reload(); });
  return <>
    <section className="panel">
      <div className="panel-title"><div><small>Abuse control</small><h2>Block a sender</h2></div></div>
      <p>Blocked senders are rejected by the mail gateway for every customer domain.</p>
      <form className="filter-row" onSubmit={add}>
        <select value={kind} onChange={(event) => setKind(event.target.value as 'address' | 'domain')} aria-label="Block type"><option value="domain">Domain</option><option value="address">Address</option></select>
        <input value={pattern} onChange={(event) => setPattern(event.target.value)} placeholder={kind === 'domain' ? 'sender.example' : 'bot@sender.example'} aria-label="Pattern" required />
        <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason" aria-label="Reason" required />
        <button className="button small">Block</button>
      </form>
      {(action.error || entries.error) && <Banner>{action.error || entries.error}</Banner>}
    </section>
    <DataTable title="Sender denylist" eyebrow="Blocked" rows={entries.rows} rowKey={(row) => row.id} columns={[
      { key: 'pattern', label: 'Pattern', render: (row) => <><code>{row.pattern}</code><small>{row.kind}</small></> },
      { key: 'reason', label: 'Reason', render: (row) => row.reason },
      { key: 'by', label: 'Added by', render: (row) => <>{row.created_by}<small>{formatDate(row.created_at)}</small></> },
      { key: 'actions', label: '', align: 'right', render: (row) => <button onClick={() => void unblock(row)}>Remove</button> },
    ]} empty="No blocked senders." />
  </>;
}

function AuditTab({ refreshKey }: { refreshKey: number }) {
  const [query, setQuery] = useState('');
  const audit = useRows(`/api/admin/audit${query ? `?action=${encodeURIComponent(query)}` : ''}`, 'events', refreshKey);
  return <>
    <SearchRow placeholder="Filter by action, e.g. billing. or domain." onSearch={setQuery} />
    {audit.error && <Banner>{audit.error}</Banner>}
    <DataTable title="Audit log" eyebrow="Latest 200 events" rows={audit.rows} rowKey={(row) => row.id} columns={[
      { key: 'action', label: 'Action', render: (row) => <b>{row.action}</b> },
      { key: 'actor', label: 'Actor', render: (row) => <>{row.actor_id || 'system'}<small>{row.actor_type}</small></> },
      { key: 'target', label: 'Target', render: (row) => row.target_type ? <>{row.target_type}<small><code>{row.target_id}</code></small></> : '—' },
      { key: 'meta', label: 'Details', render: (row) => row.metadata ? <code className="meta">{String(row.metadata).slice(0, 140)}</code> : '—' },
      { key: 'time', label: 'Time', render: (row) => formatDate(row.created_at) },
    ]} empty="No matching events." />
  </>;
}

/* ---------- Table ---------- */

type Column<T> = { key: string; label: string; render: (row: T) => ReactNode; align?: 'right' };

function DataTable<T>({ title, eyebrow, rows, columns, rowKey, empty = 'Nothing here yet.', toolbar }: {
  title: string; eyebrow: string; rows: T[] | null; columns: Column<T>[]; rowKey: (row: T) => string; empty?: string; toolbar?: ReactNode;
}) {
  return <section className="panel">
    <div className="panel-title"><div><small>{eyebrow}</small><h2>{title}</h2></div>{rows && <span>{plural(rows.length, 'row')}</span>}</div>
    {toolbar}
    {!rows ? <Loading /> : rows.length
      ? <div className="table-scroll admin-table-scroll"><table className="data-table admin-data-table">
          <thead><tr>{columns.map((column) => <th key={column.key} className={column.align}>{column.label}</th>)}</tr></thead>
          <tbody>{rows.map((row) => <tr key={rowKey(row)}>{columns.map((column) => <td key={column.key} data-label={column.label} className={column.align}>{column.render(row)}</td>)}</tr>)}</tbody>
        </table></div>
      : <Empty text={empty} />}
  </section>;
}
